import { useState, useCallback } from 'react';
import { useAuthStore } from '@/features/auth';
import { useEffectiveBusinessId } from '@/hooks';
import {
  extractExifData,
  createPreviewUrl,
  revokePreviewUrl,
  generatePhotoId,
} from '../services/exifService';
import {
  findNearestProjectSite,
  uploadPhoto,
} from '../services/photoService';
import type { PhotoFile, ProjectMatch } from '../types';

interface UsePhotoUploadOptions {
  defaultProjectId?: string | null;
  onUploadStart?: (count: number) => void;
  onUploadComplete?: (successCount: number, errorCount: number, totalCount: number) => void;
  onError?: (error: Error) => void;
  autoMatchProject?: boolean;
}

interface UsePhotoUploadReturn {
  photos: PhotoFile[];
  isProcessing: boolean;
  addFiles: (files: FileList | File[]) => Promise<void>;
  removePhoto: (photoId: string) => void;
  setSelectedProject: (photoId: string, projectId: string | null) => void;
  setDescription: (photoId: string, description: string) => void;
  uploadPhotos: () => Promise<void>;
  clearAll: () => void;
}

export function usePhotoUpload(options: UsePhotoUploadOptions = {}): UsePhotoUploadReturn {
  const { 
    defaultProjectId = null,
    onUploadStart,
    onUploadComplete, 
    onError, 
    autoMatchProject = true 
  } = options;
  
  const { user } = useAuthStore();
  const effectiveBusinessId = useEffectiveBusinessId();
  
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    if (fileArray.length === 0) return;

    setIsProcessing(true);

    try {
      // Create initial photo objects
      const newPhotos: PhotoFile[] = fileArray.map((file) => ({
        id: generatePhotoId(),
        file,
        preview: createPreviewUrl(file),
        exif: {
          takenAt: null,
          cameraMake: null,
          cameraModel: null,
          lat: null,
          lng: null,
          gpsAccuracy: null,
        },
        status: 'extracting' as const,
        suggestedProject: null,
        // If defaultProjectId is provided, pre-select it
        selectedProjectId: defaultProjectId,
      }));

      setPhotos((prev) => [...prev, ...newPhotos]);

      // Process each photo
      for (const photo of newPhotos) {
        try {
          // Extract EXIF data
          const exif = await extractExifData(photo.file);
          
          let suggestedProject: ProjectMatch | null = null;

          // Only find matching project if we don't have a default project
          if (!defaultProjectId && autoMatchProject && exif.lat && exif.lng && effectiveBusinessId) {
            try {
              const matches = await findNearestProjectSite(
                effectiveBusinessId,
                exif.lat,
                exif.lng
              );
              
              if (matches.length > 0) {
                suggestedProject = matches[0];
              }
            } catch (matchError) {
              console.error('Error finding project match:', matchError);
            }
          }

          // Update photo with extracted data
          setPhotos((prev) =>
            prev.map((p) =>
              p.id === photo.id
                ? {
                    ...p,
                    exif,
                    status: 'ready' as const,
                    suggestedProject,
                    // Keep defaultProjectId if set, otherwise use suggested or null
                    selectedProjectId: defaultProjectId || suggestedProject?.projectId || null,
                  }
                : p
            )
          );
        } catch (error) {
          console.error(`Error processing photo ${photo.id}:`, error);
          setPhotos((prev) =>
            prev.map((p) =>
              p.id === photo.id
                ? {
                    ...p,
                    status: 'error' as const,
                    error: 'Failed to process image',
                  }
                : p
            )
          );
        }
      }
    } finally {
      setIsProcessing(false);
    }
  }, [autoMatchProject, effectiveBusinessId, defaultProjectId]);

  const removePhoto = useCallback((photoId: string) => {
    setPhotos((prev) => {
      const photo = prev.find((p) => p.id === photoId);
      if (photo) {
        revokePreviewUrl(photo.preview);
      }
      return prev.filter((p) => p.id !== photoId);
    });
  }, []);

  const setSelectedProject = useCallback((photoId: string, projectId: string | null) => {
    setPhotos((prev) =>
      prev.map((p) =>
        p.id === photoId
          ? { ...p, selectedProjectId: projectId }
          : p
      )
    );
  }, []);

  const setDescription = useCallback((photoId: string, description: string) => {
    setPhotos((prev) =>
      prev.map((p) =>
        p.id === photoId
          ? { ...p, description }
          : p
      )
    );
  }, []);

  const uploadPhotos = useCallback(async () => {
    if (!effectiveBusinessId || !user?.user_profile_id) {
      onError?.(new Error('User not authenticated or no business selected'));
      return;
    }

    const readyPhotos = photos.filter((p) => p.status === 'ready');
    
    if (readyPhotos.length === 0) return;

    setIsProcessing(true);
    
    // Notify upload start
    onUploadStart?.(readyPhotos.length);

    let successCount = 0;
    let errorCount = 0;

    for (const photo of readyPhotos) {
      try {
        // Mark as uploading
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === photo.id
              ? { ...p, status: 'uploading' as const }
              : p
          )
        );

        // Use defaultProjectId if set, otherwise use selectedProjectId
        const projectIdToUse = defaultProjectId || photo.selectedProjectId;

        // Upload
        await uploadPhoto({
          file: photo.file,
          businessId: effectiveBusinessId,
          uploadedBy: user.user_profile_id,
          projectId: projectIdToUse,
          exif: photo.exif,
          suggestedProjectId: photo.suggestedProject?.projectId,
          matchDistanceM: photo.suggestedProject?.distanceM,
          matchConfidence: photo.suggestedProject?.confidence,
          description: photo.description,
        });

        // Mark as uploaded
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === photo.id
              ? { ...p, status: 'uploaded' as const }
              : p
          )
        );

        successCount++;
      } catch (error) {
        console.error(`Error uploading photo ${photo.id}:`, error);
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === photo.id
              ? {
                  ...p,
                  status: 'error' as const,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : p
          )
        );
        errorCount++;
        onError?.(error instanceof Error ? error : new Error('Upload failed'));
      }
    }

    setIsProcessing(false);

    // Notify upload complete with counts
    onUploadComplete?.(successCount, errorCount, readyPhotos.length);
  }, [photos, user, effectiveBusinessId, defaultProjectId, onUploadStart, onUploadComplete, onError]);

  const clearAll = useCallback(() => {
    photos.forEach((photo) => revokePreviewUrl(photo.preview));
    setPhotos([]);
  }, [photos]);

  return {
    photos,
    isProcessing,
    addFiles,
    removePhoto,
    setSelectedProject,
    setDescription,
    uploadPhotos,
    clearAll,
  };
}